<template>
  <full-size-page max-container>
    <a-flex gap="large" vertical>
      <a-spin v-if="isLoading" :size="'large'" style="align-self: center" />

      <LabelTemplateEditMain :loading="isLoading" />

      <LabelTemplateEditObject :loading="isLoading" />

      <LabelTemplateEditActions
        @saveClicked="onSaveClicked()"
        :loading="isLoading"
        :isCommon="isCommon"
      />
    </a-flex>
  </full-size-page>
</template>

<script setup lang="ts">
import LabelTemplateEditMain from "@/components/settings/labels/labelTemplateEdit/labelTemplateEditMain/LabelTemplateEditMain.vue";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import type { LabelTemplateDTO } from "@/api/classes/labelTemplate/labelTemplate.dto";
import { createNotification } from "@/tools/notification";
import { useLabelTemplateFormStore } from "@/stores/labelTemplate/labelTemplateFormStore";
import { useRoute, useRouter } from "vue-router";
import { useLabelTemplateStore } from "@/stores/labelTemplate/labelTemplateStore";
import FullSizePage from "@/components/base/FullSizePage.vue";
import LabelTemplateEditObject from "@/components/settings/labels/labelTemplateEdit/labelTemplateEditObject/LabelTemplateEditObject.vue";
import { storeToRefs } from "pinia";
import LabelTemplateEditActions from "@/components/settings/labels/labelTemplateEdit/labelTemplateEditActions/LabelTemplateEditActions.vue";
import { debounce } from "@/tools/debounce";
import { useConfirmChanges } from "@/composables/confirmChanges/confirmChanges";
import { usePartner } from "@/stores/partner/partner";

const labelTemplateFormStore = useLabelTemplateFormStore();
const labelTemplateStore = useLabelTemplateStore();
const partnerStore = usePartner();

const {
  labelTemplateObjectTypeField,
  labelTemplateTextTypeField,
  alignmentTypeField,
  plainTextField,
  textFontField,
  textSizeField,
  enableLineBreaksField,
  numberOfMaximumNumberOfLinesField,
  plainTextPositionField,
  domainObjectPropertyTypeField,
  barcodeTypeField,
  barcodeSizeField,
  barcodeHeightInMmField,
  displayBarcodeTextField,
  rightMarginField,
  leftMarginField,
  bottomMarginField,
  topMarginField,
  labelObjectOrientationField,
} = storeToRefs(labelTemplateFormStore);

const isEqual = (obj1: LabelTemplateDTO, obj2: LabelTemplateDTO): boolean => {
  if (obj1 === obj2) return true;
  if (typeof obj1 !== "object" || typeof obj2 !== "object") return false;

  const keys1 = (Object.keys(obj1) as (keyof LabelTemplateDTO)[]).sort();
  const keys2 = (Object.keys(obj2) as (keyof LabelTemplateDTO)[]).sort();

  if (keys1.length !== keys2.length) return false;
  if (keys1.join() !== keys2.join()) return false;

  return keys1.every((key) => isEqual(obj1[key], obj2[key]));
};

const isFieldsChanged = computed(
  () =>
    !!(
      initialLabelState.value &&
      currentLabelState.value &&
      !isEqual(initialLabelState.value, currentLabelState.value)
    ),
);

const initialLabelState = ref<LabelTemplateDTO | null>(null);
const currentLabelState = ref<LabelTemplateDTO | null>(null);

const refs = storeToRefs(labelTemplateFormStore);
const reactiveState = reactive(refs);

const router = useRouter();
const route = useRoute();

const isLoading = ref(true);
const isCommon = computed(() => !!(route.query.isCommon || null));

const initComponent = async () => {
  if (isCommon.value && !labelTemplateStore.isAdminAccount) {
    return await router.push({ name: "labels" });
  }

  isLoading.value = true;
  await labelTemplateStore.getAllPersonalLabelTemplates();

  const labelId = route.params.labelId as string | "create";
  const cloneId = route.query.cloneId || null;
  const shareId = route.query.shareId || null;
  if (cloneId) {
    if (!partnerStore.partner?.id) {
      createNotification("error", "Ошибка", "Не найден id партнера");
      return;
    }
    await labelTemplateStore.getAllCommonLabelTemplates();
  }

  await labelTemplateStore.getProductsForPreviewOptions();

  let label: LabelTemplateDTO | null | undefined = cloneId
    ? labelTemplateStore.allCommonLabels.find((label) => label.id === cloneId)
    : shareId
      ? labelTemplateStore.allPersonalLabels.find(
          (label) => label.id === shareId,
        )
      : labelId !== "create"
        ? (isCommon.value
            ? labelTemplateStore.allCommonLabels
            : labelTemplateStore.allPersonalLabels
          ).find((label) => label.id === labelId)
        : null;

  if (labelId === "create" && !cloneId && !shareId) {
    label = labelTemplateFormStore.createLabelTemplateFromState();
    labelTemplateFormStore.currentEditingLabel = "";
  } else {
    if ((!labelId && !cloneId && !shareId) || !label) {
      createNotification("error", "Ошибка", "Этикетка не найдена");

      return await router.push({ name: "labels" });
    }
  }

  if (label) {
    if (cloneId || shareId) {
      label.partnerId = partnerStore.partner!.id;
      label.id = "";
    }

    await labelTemplateFormStore.fillFields(label);

    initialLabelState.value = label;

    labelTemplateFormStore.currentEditingLabel = label.id;
  }

  setTimeout(() => {
    isLoading.value = false;
  });
};

useConfirmChanges(isFieldsChanged);

onMounted(async () => {
  await initComponent();
});

onUnmounted(() => {
  labelTemplateFormStore.resetFullState();
});

const onSaveClicked = () => {
  initialLabelState.value =
    labelTemplateFormStore.createLabelTemplateFromState();

  currentLabelState.value = initialLabelState.value;
};

watch(
  [
    labelTemplateObjectTypeField,
    labelTemplateTextTypeField,
    alignmentTypeField,
    plainTextField,
    textFontField,
    textSizeField,
    enableLineBreaksField,
    numberOfMaximumNumberOfLinesField,
    plainTextPositionField,
    domainObjectPropertyTypeField,
    barcodeTypeField,
    barcodeSizeField,
    barcodeHeightInMmField,
    displayBarcodeTextField,
    rightMarginField,
    leftMarginField,
    bottomMarginField,
    topMarginField,
    labelObjectOrientationField,
  ],
  () => {
    labelTemplateFormStore.saveSelectedLabelTemplateObject();
  },
);

watch(
  reactiveState,
  debounce(() => {
    currentLabelState.value =
      labelTemplateFormStore.createLabelTemplateFromState();
  }, 500),
  { deep: true },
);
</script>

<style scoped lang="scss"></style>
